<script setup>
/* global axios */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Modal from 'dashboard/components/Modal.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

const showModal = ref(true);
const qrcode = ref(null);
const connectionStatus = ref('connecting');
const errored = ref(false);
let pollTimer = null;

const baseUrl = () =>
  `/api/v1/accounts/${accountId.value}/inboxes/${props.inboxId}/uazapi`;

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};

const finishConnected = () => {
  stopPolling();
  store.dispatch('inboxes/updateInboxConnectionStatus', {
    id: props.inboxId,
    connection_status: 'connected',
  });
  emit('close');
};

const pollStatus = async () => {
  try {
    const { data } = await axios.get(`${baseUrl()}/status`);
    if (data.status) connectionStatus.value = data.status;
    if (data.qrcode) qrcode.value = data.qrcode;
    if (data.status === 'connected') finishConnected();
  } catch {
    // ignora erro de polling
  }
};

const startConnect = async () => {
  errored.value = false;
  try {
    const { data } = await axios.post(`${baseUrl()}/connect`);
    if (data.qrcode) qrcode.value = data.qrcode;
    if (data.status) connectionStatus.value = data.status;
    pollTimer = setInterval(pollStatus, 3000);
  } catch {
    errored.value = true;
  }
};

const close = () => {
  stopPolling();
  emit('close');
};

onMounted(startConnect);
onBeforeUnmount(stopPolling);
</script>

<template>
  <Modal v-model:show="showModal" :on-close="close">
    <div class="flex flex-col items-center gap-4 p-8">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.WHATSAPP_QR.RECONNECT.MODAL_TITLE') }}
      </h3>

      <p v-if="errored" class="text-sm text-n-ruby-9">
        {{ t('INBOX_MGMT.ADD.WHATSAPP_QR.RECONNECT.ERROR') }}
      </p>

      <template v-else>
        <p class="text-center text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP_QR.RECONNECT.SCAN_INSTRUCTION') }}
        </p>
        <img
          v-if="qrcode"
          :src="qrcode"
          alt="WhatsApp QR Code"
          class="h-56 w-56 rounded-lg border border-n-weak"
        />
        <p v-else class="text-sm text-n-slate-10">
          {{ t('INBOX_MGMT.ADD.WHATSAPP_QR.RECONNECT.WAITING_QR') }}
        </p>
      </template>

      <button
        class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
        @click="close"
      >
        {{ t('INBOX_MGMT.ADD.WHATSAPP_QR.RECONNECT.CLOSE') }}
      </button>
    </div>
  </Modal>
</template>
